import './DashboardNavbar/DashboardNavbarConfig';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import baselineMenu from '@iconify/icons-ic/baseline-menu';
import { Icon } from '@iconify/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AccountPopover from './AccountPopover';
import { AppBarStyled, ToolbarStyled, ButtonToolbarStyled, GridContainerStyled } from './styles/DashboardNavbarStyles';
import { kanbanJobTitleId, kanbanProficiencyLevel, kanbanSkillType } from '../../cache/cache';
import { renderNotification } from '../../cache/learner/homeReactiveVars';
import { kanbanDataVar } from '../../cache/learner/kanbanReactiveVar';
import AtomProgress from '../../components/atoms/AtomProgress';
import TranslationComponent from '../../components/generics/TranslationComponent/TranslationComponent';
import useKanbanDataHook from '../../hooks/kanbanDataHook/useKanbanDataHook';
import userAuth from '../../hooks/userAuth';
import useSetDataFromLocalStorage from '../../hooks/useSetDataFromLocalStorage/useSetDataFromLocalStorage';
import { useLazyGetStudentKanbanSkilltrackerAll } from '../../operations/query/students/useGetStudentKanbanSkillTrackerAll/useGetStudentKanbanSkilltrackerAll';
import { useQueryGetStudentNotification } from '../../operations/query/students/useGetStudentNotification';
import NotificationComponent from '../components/NotificationComponent/NotificationComponent';

type DashboardNavbarProps = {
  onOpenSidebar: VoidFunction;
  openChangePasswordDialog: VoidFunction;
};

const DashboardNavbar: React.FC<DashboardNavbarProps> = ({ onOpenSidebar, openChangePasswordDialog }) => {
  const { t } = useTranslation('DashboardNavbar');
  const { auth } = userAuth();
  const { data: studentNotificationData, loading: studentNotificationLoading } = useQueryGetStudentNotification(
    auth?.student?.id ?? '',
  );
  const { operations: kanbanOps } = useKanbanDataHook(
    kanbanDataVar,
    kanbanSkillType,
    kanbanJobTitleId,
    kanbanProficiencyLevel,
  );
  const { operations: setDataLocalOps } = useSetDataFromLocalStorage();
  const [getKanbanData, { data: dataKanban }] = useLazyGetStudentKanbanSkilltrackerAll(true);
  const smScreen: boolean = useMediaQuery(useTheme().breakpoints.down('sm'));

  const loadKanbanData = (): void => {
    getKanbanData({
      variables: {
        filters: ['specialized', 'human'],
        studentId: auth?.student?.id ?? '',
      },
    });
  };

  useEffect(() => {
    if (!studentNotificationLoading && studentNotificationData?.getStudentNotification) {
      renderNotification(studentNotificationData?.getStudentNotification);
    }
  }, [studentNotificationLoading]);

  useEffect(() => {
    if (auth?.student?.id) {
      if (setDataLocalOps.setDataFromLocal()) {
        return;
      }
      loadKanbanData();
    }
  }, []);

  useEffect(() => {
    if (dataKanban?.getStudentKanbanSkilltrackerAll) {
      kanbanOps.setKanbanData(dataKanban?.getStudentKanbanSkilltrackerAll);
    }
  }, [dataKanban]);

  return (
    <AppBarStyled role="banner" aria-label={t('HEADER_ARIA_LABEL')} title="1Mentor">
      <ToolbarStyled>
        <ButtonToolbarStyled
          aria-label={t('MENU_BUTTON_ARIA_LABEL')}
          title={t('MENU_BUTTON_ARIA_LABEL')}
          onClick={onOpenSidebar}
          id="sidebar-open-button"
          data-testid="sidebar-open-button"
        >
          <Icon icon={!smScreen ? menu2Fill : baselineMenu} style={{ padding: 'px' }} />
        </ButtonToolbarStyled>
        <Box component="nav" role="navigation" aria-label={t('NAV_ARIA_LABEL')}>
          <GridContainerStyled>
            <Box component="div">
              <TranslationComponent />
            </Box>
            {studentNotificationLoading ? (
              <Box component="div" aria-label={t('NOTIFICATIONS_ARIA_LABEL')}>
                <AtomProgress
                  visible
                  iconVariant="small"
                  layout="inline-silent"
                  background="grey"
                  data-testid="circular_loader"
                />
              </Box>
            ) : (
              <Box component="div" aria-label={t('NOTIFICATIONS_ARIA_LABEL')}>
                <NotificationComponent />
              </Box>
            )}
            <Box component="div" aria-label={t('ACCOUNT_MENU_ARIA_LABEL')}>
              <AccountPopover openChangePasswordDialog={openChangePasswordDialog} />
            </Box>
          </GridContainerStyled>
        </Box>
      </ToolbarStyled>
    </AppBarStyled>
  );
};

export default DashboardNavbar;
